/**
 * Variable resolution utilities for campaign preview
 * Handles substitution of template variables with actual subscriber data
 */

import { get } from 'lodash-es';

import { BaseCampaignSender, CampaignSubscriber } from '@/shared/types/campaigns';
import { Option } from '@/shared/types/rules';

import { getCampaignContextVariableOptions, CampaignForm } from '../utils';

/**
 * Extract variable reference from parameter value
 * Parameter can be a string like "{{$contact.name}}" or an Option object
 */
function extractVariableFromParameter(parameter: { name: string | Option }): string | null {
  const name = typeof parameter.name === 'string' ? parameter.name : parameter.name?.value;

  if (!name || typeof name !== 'string') {
    return null;
  }

  // Check if it's a context variable {{$...}}
  const match = name.match(/\{\{\$([^}]+)\}\}/);
  return match ? match[1] : null;
}

/**
 * Resolves WhatsApp template positional parameters ({{1}}, {{2}}, etc.)
 *
 * @param content - Template text with {{1}}, {{2}} placeholders
 * @param parameters - Array of parameters to substitute
 * @param context - Subscriber context for variable resolution
 * @returns Content with parameters resolved
 */
export function resolveWhatsAppParameters(
  content: string,
  parameters: Array<{ name: string | Option }> = [],
  context?: Record<string, unknown>
): string {
  if (!content || !parameters || parameters.length === 0) {
    return content;
  }

  // Replace {{1}}, {{2}}, {{3}}, etc. with parameter values
  return content.replace(/\{\{(\d+)\}\}/g, (match, index: string) => {
    const paramIndex = parseInt(index, 10) - 1; // {{1}} maps to parameters[0]

    if (paramIndex < 0 || paramIndex >= parameters.length) {
      return match; // Parameter not found - preserve original
    }

    const parameter = parameters[paramIndex];

    // Check if parameter references a context variable
    const variablePath = extractVariableFromParameter(parameter);

    if (variablePath && context) {
      // Resolve from subscriber context
      const value = get(context, variablePath);

      if (value === null || value === undefined) {
        return match; // Variable not found - preserve original
      }

      return String(value);
    }

    // Use parameter value directly (static text)
    const name = typeof parameter.name === 'string' ? parameter.name : parameter.name?.value;
    return name || match;
  });
}

/**
 * Resolves campaign template variables with subscriber context data
 *
 * @param content - Template content with {{$variable}} placeholders
 * @param context - Subscriber context data object
 * @returns Content with variables replaced, or original content if no context
 */
export function resolveVariables(content: string, context?: Record<string, unknown>): string {
  // No context provided - return original content
  if (!context || typeof context !== 'object') {
    return content;
  }

  // No content - return as is
  if (!content || typeof content !== 'string') {
    return content;
  }

  // Replace all {{$variable.path}} patterns
  return content.replace(/\{\{\$([^}]+)\}\}/g, (match, variablePath: string) => {
    const cleanPath = variablePath.trim();

    // Invalid path - preserve original pattern
    if (!cleanPath || cleanPath.includes('..')) {
      return match;
    }

    // Get the value from context
    const value = get(context, cleanPath);

    // Value not found - preserve original pattern
    if (value === null || value === undefined) {
      return match;
    }

    // Convert value to string
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return match;
      }
    }

    return String(value);
  });
}

/**
 * Extract unresolved variable references from content.
 * Returns variable names (without `{{$}}` wrapper) that are NOT in the available set.
 */
export const extractUnresolvedVariables = (
  content: string,
  availableVariables: Set<string>
): string[] => {
  if (!content) {
    return [];
  }

  const unresolved: string[] = [];
  for (const match of content.matchAll(/\{\{\$([^}]+)\}\}/g)) {
    const varName = match[1].trim();
    if (!availableVariables.has(varName)) {
      unresolved.push(varName);
    }
  }
  return unresolved;
};

/**
 * Get unresolved variables for a campaign sender.
 * Checks all variable references in the sender's content/parameters against available context variables.
 *
 * @param sender - Campaign sender to validate
 * @param availableVariables - Set of variable names (without `{{$}}`) that exist in subscriber contexts
 * @returns Array of unresolved variable names
 */
export const getUnresolvedSenderVariables = (
  sender: BaseCampaignSender,
  availableVariables: Set<string>
): string[] => {
  if (!sender.integrationId) {
    return [];
  }

  if (sender.integrationType === 'whatsapp') {
    const allParams = [
      ...(sender.headerParameters || []),
      ...(sender.bodyParameters || []),
      ...(sender.footerParameters || []),
    ];

    const unresolved: string[] = [];
    for (const param of allParams) {
      const value = typeof param.name === 'string' ? param.name : (param.name as Option)?.value;
      if (value) {
        unresolved.push(...extractUnresolvedVariables(value, availableVariables));
      }
    }

    // Check headerMediaParam value
    const mediaValue = sender.headerMediaParam?.value;
    if (mediaValue) {
      const mediaStr = typeof mediaValue === 'string' ? mediaValue : (mediaValue as Option)?.value;
      if (mediaStr) {
        unresolved.push(...extractUnresolvedVariables(mediaStr, availableVariables));
      }
    }

    return [...new Set(unresolved)];
  }

  // SMS, RCS, Voice, Email: check content + subject
  const unresolved: string[] = [];
  unresolved.push(...extractUnresolvedVariables(sender.content || '', availableVariables));
  if (sender.subject) {
    unresolved.push(...extractUnresolvedVariables(sender.subject, availableVariables));
  }
  return [...new Set(unresolved)];
};

/**
 * Validate all senders in a campaign form against subscriber context variables.
 * Returns deduplicated list of unresolved variable names across all senders.
 */
export const getFormUnresolvedVariables = (
  formData: CampaignForm,
  subscribers: CampaignSubscriber[]
): string[] => {
  const variableOptions = getCampaignContextVariableOptions(subscribers);
  const availableVars = new Set(
    variableOptions.filter((opt) => opt.type === 'available_variables').map((opt) => opt.label)
  );

  const all = [
    formData.primarySender,
    ...(formData.fallbackSenders?.filter((s) => s?.integrationId) || []),
  ].flatMap((sender) => getUnresolvedSenderVariables(sender, availableVars));

  return [...new Set(all)];
};
